/**
 * Lightweight markdown → safe React nodes for vault document Read view.
 * Escapes HTML; supports headings, lists, tables, code, links, bold/italic.
 */
import React from "react";

function escapeHtml(s) {
  return String(s || "")
    .replace(/&/g, "&amp;")
    .replace(/</g, "&lt;")
    .replace(/>/g, "&gt;")
    .replace(/"/g, "&quot;");
}

function inlineFormat(text) {
  const escaped = escapeHtml(text);
  const withCode = escaped.replace(/`([^`]+)`/g, "<code>$1</code>");
  const withBold = withCode.replace(/\*\*([^*]+)\*\*/g, "<strong>$1</strong>");
  const withItalic = withBold.replace(/\*([^*\n]+)\*/g, "<em>$1</em>");
  const withLinks = withItalic.replace(
    /\[([^\]]+)\]\((https?:[^)\s]+)\)/g,
    '<a href="$2" target="_blank" rel="noopener noreferrer">$1</a>',
  );
  return withLinks;
}

export function VaultMarkdownView({ markdown, sx = {} }) {
  const src = String(markdown || "").replace(/\r\n/g, "\n");
  if (!src.trim()) {
    return (
      <p style={{ color: "#6b7280", fontSize: 14, margin: 0 }}>No document body yet.</p>
    );
  }

  const lines = src.split("\n");
  const blocks = [];
  let i = 0;
  let key = 0;

  const pushHtml = (tag, html, style) => {
    blocks.push(
      React.createElement(tag, {
        key: key++,
        style,
        dangerouslySetInnerHTML: { __html: html },
      }),
    );
  };

  while (i < lines.length) {
    const line = lines[i];

    if (line.startsWith("```")) {
      const fence = [];
      i += 1;
      while (i < lines.length && !lines[i].startsWith("```")) {
        fence.push(lines[i]);
        i += 1;
      }
      i += 1;
      blocks.push(
        <pre
          key={key++}
          style={{
            background: "#f3f4f6",
            padding: "12px 14px",
            borderRadius: 8,
            overflow: "auto",
            fontSize: 13,
            lineHeight: 1.45,
          }}
        >
          <code>{fence.join("\n")}</code>
        </pre>,
      );
      continue;
    }

    if (/^\|(.+)\|$/.test(line.trim()) && i + 1 < lines.length && /^\|[\s:|-]+\|$/.test(lines[i + 1].trim())) {
      const rows = [];
      while (i < lines.length && /^\|(.+)\|$/.test(lines[i].trim())) {
        const cells = lines[i]
          .trim()
          .slice(1, -1)
          .split("|")
          .map((c) => c.trim());
        if (!/^[\s:|-]+$/.test(cells.join(""))) rows.push(cells);
        i += 1;
      }
      blocks.push(
        <div key={key++} className="vault-md-table-wrap">
          <table className="vault-md-table">
            <tbody>
              {rows.map((row, ri) => (
                <tr key={ri}>
                  {row.map((cell, ci) => {
                    const Tag = ri === 0 ? "th" : "td";
                    return (
                      <Tag
                        key={ci}
                        className={ri === 0 ? "vault-md-th" : "vault-md-td"}
                        dangerouslySetInnerHTML={{ __html: inlineFormat(cell) }}
                      />
                    );
                  })}
                </tr>
              ))}
            </tbody>
          </table>
        </div>,
      );
      continue;
    }

    const heading = /^(#{1,3})\s+(.+)$/.exec(line);
    if (heading) {
      const level = heading[1].length;
      const tag = level === 1 ? "h1" : level === 2 ? "h2" : "h3";
      const sizes = { h1: 22, h2: 18, h3: 16 };
      pushHtml(tag, inlineFormat(heading[2]), {
        fontSize: sizes[tag],
        fontWeight: 700,
        margin: "16px 0 8px",
        lineHeight: 1.3,
      });
      i += 1;
      continue;
    }

    if (/^[-*]\s+/.test(line)) {
      const items = [];
      while (i < lines.length && /^[-*]\s+/.test(lines[i])) {
        items.push(lines[i].replace(/^[-*]\s+/, ""));
        i += 1;
      }
      blocks.push(
        <ul key={key++} style={{ margin: "8px 0", paddingLeft: 22 }}>
          {items.map((item, idx) => (
            <li
              key={idx}
              style={{ marginBottom: 4, fontSize: 14, lineHeight: 1.5 }}
              dangerouslySetInnerHTML={{ __html: inlineFormat(item) }}
            />
          ))}
        </ul>,
      );
      continue;
    }

    if (/^\d+\.\s+/.test(line)) {
      const items = [];
      while (i < lines.length && /^\d+\.\s+/.test(lines[i])) {
        items.push(lines[i].replace(/^\d+\.\s+/, ""));
        i += 1;
      }
      blocks.push(
        <ol key={key++} style={{ margin: "8px 0", paddingLeft: 22 }}>
          {items.map((item, idx) => (
            <li
              key={idx}
              style={{ marginBottom: 4, fontSize: 14, lineHeight: 1.5 }}
              dangerouslySetInnerHTML={{ __html: inlineFormat(item) }}
            />
          ))}
        </ol>,
      );
      continue;
    }

    if (!line.trim()) {
      i += 1;
      continue;
    }

    const para = [];
    while (i < lines.length && lines[i].trim() && !lines[i].startsWith("#") && !lines[i].startsWith("```") && !/^[-*]\s+/.test(lines[i]) && !/^\d+\.\s+/.test(lines[i]) && !/^\|/.test(lines[i])) {
      para.push(lines[i]);
      i += 1;
    }
    pushHtml("p", inlineFormat(para.join(" ")), {
      fontSize: 14,
      lineHeight: 1.6,
      margin: "0 0 10px",
    });
  }

  return (
    <div className="vault-md-doc" style={{ color: "#111827", ...sx }}>
      <style>{`
        .vault-md-doc code {
          font-family: ui-monospace, SFMono-Regular, Menlo, Monaco, Consolas, monospace;
          font-size: 0.92em;
          background: #f3f4f6;
          padding: 0.1em 0.35em;
          border-radius: 4px;
          word-break: break-word;
          overflow-wrap: anywhere;
          white-space: pre-wrap;
        }
        .vault-md-table-wrap {
          width: 100%;
          max-width: 100%;
          overflow-x: auto;
          margin: 12px 0;
        }
        .vault-md-table {
          width: 100%;
          max-width: 100%;
          border-collapse: collapse;
          table-layout: fixed;
          font-size: 14px;
        }
        .vault-md-th,
        .vault-md-td {
          border: 1px solid #e5e7eb;
          padding: 8px 10px;
          text-align: left;
          vertical-align: top;
          word-break: break-word;
          overflow-wrap: anywhere;
          hyphens: auto;
        }
        .vault-md-th {
          font-weight: 700;
          background: #f9fafb;
        }
        .vault-md-doc pre {
          white-space: pre-wrap;
          word-break: break-word;
          overflow-wrap: anywhere;
        }
        @media print {
          .vault-md-table-wrap {
            overflow: visible !important;
          }
          .vault-md-table {
            font-size: 11px;
          }
          .vault-md-th,
          .vault-md-td {
            padding: 5px 6px;
          }
          .vault-md-doc code {
            background: transparent;
            padding: 0;
          }
        }
      `}</style>
      {blocks}
    </div>
  );
}

export function isDocumentLikeEntry(entryType) {
  return ["howto", "technical_ref", "document", "note_plan", "registry", "media"].includes(
    entryType,
  );
}

export { buildVaultPrintHtml, printVaultDocumentOnly } from "./vaultPrintHtml";
